import React, { useState } from 'react'
import {
  Button,
  Input,
  Option,
  Select,
  Spinner,
  Textarea,
  Tooltip,
  Typography,
} from '@material-tailwind/react'
import { Trans, useTranslation } from 'react-i18next'
import { PiInfoLight } from 'react-icons/pi'
import { logger } from '../utils/helpers/logger'
import { useFormData } from '../hooks/form'
import { useGeneralToast } from '../hooks/toast'
import { useDarkMode } from '../contexts/DarkModeContext'
import { AgamiExperience } from '../types/supabase'
import { updateExperienceDetails } from '../utils/helpers/upload.helpers'
import { CONTENT_LANGUAGES } from '../utils/constants/language.constants'
import expStartScreen from '../assets/png/exp_start_screen.png'
import { ExperienceCreditsEditor } from '../ExperienceDashboard/ExperienceCreditsEditor'

interface EditExperienceFormProps {
  experienceDetails: AgamiExperience
  refetchExperienceDetails: () => void
  closeDialog?: () => void
}

const EditExperienceForm: React.FC<EditExperienceFormProps> = ({
  experienceDetails,
  refetchExperienceDetails,
  closeDialog,
}) => {
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()
  const { success: toastSuccess, error: toastError } = useGeneralToast()
  const [loading, setLoading] = useState(false)

  const { formData, handleDataChange } = useFormData(experienceDetails)

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    e.stopPropagation()

    setLoading(true)

    const { id, name, main_language, welcome_message, description, credits_enabled, credits_blocks } =
      formData

    const { error } = await updateExperienceDetails({
      expNewData: {
        id,
        name,
        main_language,
        welcome_message,
        description,
        ...(typeof credits_enabled !== 'undefined' && { credits_enabled }),
        ...(typeof credits_blocks !== 'undefined' && { credits_blocks }),
      },
    })

    if (error) {
      toastError(t('dashboardExperienceDetail.errorGeneric'))
      logger.error(error, 'EditExperience')
      setLoading(false)
      return
    }

    await refetchExperienceDetails()
    setLoading(false)
    if (closeDialog) closeDialog()
    toastSuccess(t('dashboardExperienceDetail.successDetailsUpdated'))
  }

  return (
    <form className="flex flex-col gap-4 px-2" onSubmit={onSubmit} data-testid="experience-form">
      <div className="flex flex-col gap-y-4">
        {/* <Typography className="mb-2 font-bold">
          {t('dashboardExperienceDetail.languageSectionTitle')}
        </Typography> */}

        <div data-testid="experience-name-input">
          <Input
            value={formData.name ?? ''}
            onChange={(e) => handleDataChange('name', e.currentTarget.value)}
            type="text"
            label={t('forms.experienceName')}
            {...(darkMode && { color: 'blue-gray' })}
            required
            data-testid="experience-name-input"
          />
        </div>

        <div className="gap-y-2" data-testid="experience-main-language-select">
          <Select
            label={t('dashboardExperienceDetail.mainLanguageLabel')}
            onChange={(value) => {
              const lang = value === 'unset' ? null : value
              handleDataChange('main_language', lang ?? undefined)
            }}
            value={formData.main_language ?? 'unset'}
            disabled={loading}
            menuProps={{ className: 'dark:bg-primary' }}
            {...(darkMode && { color: 'blue-gray' })}
            labelProps={{
              className:
                'text-gray-600 dark:!text-gray-200 peer-focus:!text-gray-800 dark:peer-focus:!text-gray-100',
            }}
          >
            {/* Optionally add an explicit "Not selected" option if needed */}
            {/* <Option value="unset">{t('dashboardExperienceDetail.notSelected')}</Option> */}
            {CONTENT_LANGUAGES.map((lang) => {
              const isSelected = lang.iso === (formData.main_language ?? 'unset')
              return (
                <Option
                  key={lang.iso}
                  value={lang.iso}
                  className={` dark:hover:bg-primary-300 ${
                    isSelected ? 'bg-gray-200 dark:bg-primary-300' : ''
                  }`}
                >
                  <span className="text-primary dark:text-white">{lang.label}</span>
                </Option>
              )
            })}
          </Select>
          <Typography variant="small" className="mt-2">
            {t('dashboardExperienceDetail.mainLanguageHelp')}
          </Typography>
        </div>
      </div>

      <div className="flex justify-start items-center gap-1">
        <Typography className="font-bold">
          {t('dashboardExperienceDetail.startScreenSectionTitle')}
        </Typography>
        <Tooltip
          content={
            <div className="w-[300px]">
              <Typography variant="small" className="font-bold">
                <Trans i18nKey="dashboardExperienceDetail.startScreenHelp">
                  {/* rich text handled by i18next if desired */}
                </Trans>
              </Typography>
              <img
                src={expStartScreen}
                title={t('dashboardExperienceDetail.startScreenImageTitle')}
                alt={t('dashboardExperienceDetail.startScreenImageTitle')}
                className="opacity-90 mt-2 w-[200px] mx-auto"
              />
            </div>
          }
          placement="right"
          interactive
        >
          <span>
            <PiInfoLight className="text-primary dark:text-base-gray-400" />
          </span>
        </Tooltip>
      </div>

      <div className="flex flex-col gap-3">
        <Input
          value={formData.welcome_message ?? ''}
          label={t('dashboardExperienceDetail.welcomeMessageLabel')}
          maxLength={50}
          onChange={(e) => handleDataChange('welcome_message', e.currentTarget.value ?? '')}
          data-testid="experience-welcome-message-input"
          {...(darkMode && { color: 'white' })}
        />

        <Textarea
          className="text-primary-600 focus:text-primary-700 dark:!text-base-gray-500 dark:focus:text-base-gray-400"
          value={formData.description ?? ''}
          label={t('dashboardExperienceDetail.descriptionLabel')}
          maxLength={300}
          onChange={(e) => handleDataChange('description', e.currentTarget.value ?? '')}
          data-testid="experience-description-input"
          {...(darkMode && { color: 'blue-gray' })}
          labelProps={{
            className:
              'text-gray-600 dark:!text-gray-200 peer-focus:!text-gray-800 dark:peer-focus:!text-gray-100',
          }}
        />
      </div>

      {/* Credits Editor */}
      <ExperienceCreditsEditor
        creditsEnabled={formData.credits_enabled ?? false}
        creditsBlocks={formData.credits_blocks ?? []}
        onToggleCredits={(value) => handleDataChange('credits_enabled', value)}
        onUpdateBlocks={(blocks) => handleDataChange('credits_blocks', blocks)}
        experienceId={experienceDetails.id}
      />

      <Button type="submit" disabled={loading || !formData.name} data-testid="experience-submit-btn">
        {loading ? <Spinner className="mx-auto" /> : t('forms.updateExperience')}
      </Button>
    </form>
  )
}

export default EditExperienceForm
